<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";

import type { BreadcrumbItem } from "@/components/ui/BreadcrumbNavigation.vue";
import AppToolbar from "@/layouts/AppToolbar.vue";
import { useRemainingHeight } from "@/services/remainingHeight";

const { breadcrumbItems } = defineProps<{ breadcrumbItems: BreadcrumbItem[] }>();
const { remainingHeight, computeRemainingHeight } = useRemainingHeight(".default-page");
const bottomBanner = useTemplateRef<HTMLDivElement>("bottomBanner");
const bottomBannerHeight = ref(0);

const bottomBannerResizeObserver = new ResizeObserver((entries) => {
  const [banner] = entries;
  if (banner) {
    bottomBannerHeight.value = banner.borderBoxSize[0].blockSize;
  }
});

onMounted(() => {
  if (bottomBanner.value) {
    bottomBannerResizeObserver.observe(bottomBanner.value);
  }
});

onBeforeUnmount(() => {
  if (bottomBanner.value) {
    bottomBannerResizeObserver.unobserve(bottomBanner.value);
  }
});
</script>

<template>
  <div
    class="default-layout-container"
    :style="{
      '--remaining-height': `${remainingHeight}px`,
      '--bottom-banner-height': `${bottomBannerHeight}px`,
    }"
  >
    <AppToolbar :breadcrumb-items="breadcrumbItems" @vue:mounted="computeRemainingHeight" />
    <main class="default-page">
      <slot></slot>
    </main>
    <div v-if="$slots.bottomBanner" class="bottom-banner" ref="bottomBanner">
      <slot name="bottomBanner" />
    </div>
  </div>
</template>

<style scoped>
.default-layout-container {
  --page-remaining-height: calc(
    var(--remaining-height) - var(--bottom-banner-height) - var(--spacing-12) * 2
  );

  .app-toolbar {
    position: sticky;
    z-index: 2;
    top: 0;
  }

  .default-page {
    z-index: 1;
    padding: var(--spacing-12) var(--page-padding);
  }

  .bottom-banner {
    position: fixed;
    z-index: 2;
    bottom: 0;
    width: 100dvw;
    padding: var(--spacing-12) var(--page-padding);
    border-top: solid var(--stroke-width-m) var(--color-stroke-background-primary);
    background-color: var(--color-background-primary);
  }
}
</style>
